import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useSocieta } from '../../../context/SocietaContext';

// ── Types ────────────────────────────────────────────────────────────────────

interface UtenteDisp  { id: number; nome: string; email: string; societaIds: number[]; sedeIds: number[]; }
interface SedeDisp    { id: number; nome: string; societaId: number; societaNome: string; nUtenti: number; }
interface SocietaDisp { id: number; nome: string; nUtenti: number; }

interface DestinatariResp {
  utenti: UtenteDisp[];
  sedi: SedeDisp[];
  societa: SocietaDisp[];
  societaIdDefault: number | null;
}

const PRIORITA = ['URGENTE', 'ALTA', 'MEDIA', 'BASSA'] as const;
const PRIORITA_LABEL: Record<string, string> = { URGENTE: 'Urgente', ALTA: 'Alta', MEDIA: 'Media', BASSA: 'Bassa' };
const PRIORITA_COLOR: Record<string, string> = { URGENTE: '#ef4444', ALTA: '#f97316', MEDIA: '#3b82f6', BASSA: '#6b7280' };

// ── Component ────────────────────────────────────────────────────────────────

export default function InvioMessaggi() {
  const navigate = useNavigate();
  const { societaAttiva } = useSocieta();

  // Dati destinatari disponibili
  const [disp, setDisp] = useState<DestinatariResp | null>(null);
  const [loadingDest, setLoadingDest] = useState(true);

  // Selezione criteri
  const [societaSel, setSocietaSel] = useState<number[]>([]);
  const [sediSel, setSediSel] = useState<number[]>([]);
  // Modifiche manuali nella tab "Utenti specifici"
  const [utentiEsclusi, setUtentiEsclusi] = useState<Set<number>>(new Set());
  const [utentiExtra, setUtentiExtra] = useState<Set<number>>(new Set());

  const [tabDest, setTabDest] = useState<'societa' | 'sedi' | 'utenti'>('societa');
  const [cercaUtente, setCercaUtente] = useState('');

  // Composizione
  const [oggetto, setOggetto] = useState('');
  const [corpo, setCorpo] = useState('');
  const [priorita, setPriorita] = useState<string>('MEDIA');

  // Allegati
  const [allegatiFile, setAllegatiFile] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState<Record<number, 'idle' | 'uploading' | 'done' | 'error'>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<number, string>>({});

  // Carica destinatari disponibili all'avvio
  useEffect(() => {
    apiFetch<DestinatariResp>('/api/messaggi/destinatari')
      .then(data => {
        setDisp({
          ...data,
          sedi:    data.sedi.map(s    => ({ ...s, nUtenti: parseInt(String(s.nUtenti), 10) || 0 })),
          societa: data.societa.map(s => ({ ...s, nUtenti: parseInt(String(s.nUtenti), 10) || 0 })),
        });
      })
      .catch((err: any) => setError(`Errore caricamento destinatari: ${err.message ?? err}`))
      .finally(() => setLoadingDest(false));
  }, []);

  // ── Filtro topbar ─────────────────────────────────────────────────────────

  // Società visibili nel tab (filtrate da societaAttiva)
  const societaVisibili = useMemo<SocietaDisp[]>(() => {
    if (!disp) return [];
    if (societaAttiva) return disp.societa.filter(s => s.id === societaAttiva.id);
    return disp.societa;
  }, [disp, societaAttiva]);

  // Sedi visibili: filtrate per societaSel (o topbar); se nessuna selezione → tutte
  const sediVisibili = useMemo<SedeDisp[]>(() => {
    if (!disp) return [];
    const filtroSoc = societaAttiva ? [societaAttiva.id] : societaSel;
    if (filtroSoc.length === 0) return disp.sedi;
    return disp.sedi.filter(s => filtroSoc.includes(s.societaId));
  }, [disp, societaAttiva, societaSel]);

  const gruppiSedi = useMemo<Record<string, SedeDisp[]>>(() => {
    const groups: Record<string, SedeDisp[]> = {};
    for (const s of sediVisibili) {
      if (!groups[s.societaNome]) groups[s.societaNome] = [];
      groups[s.societaNome].push(s);
    }
    return groups;
  }, [sediVisibili]);

  // ── Calcolo utenti finali ─────────────────────────────────────────────────

  // Set degli utenti derivati dai criteri società+sede (prima di esclusioni/extra)
  const derivedUserIds = useMemo<Set<number>>(() => {
    if (!disp) return new Set();

    const effSoc = societaAttiva ? [societaAttiva.id] : societaSel;
    const effSedi = sediSel;

    if (effSoc.length === 0 && effSedi.length === 0) return new Set();

    let matched: UtenteDisp[];

    if (effSoc.length > 0 && effSedi.length > 0) {
      // Intersezione: utente deve essere in una delle società selezionate E in una delle sedi
      matched = disp.utenti.filter(u =>
        effSoc.some(sid => u.societaIds.includes(sid)) &&
        effSedi.some(sid => u.sedeIds.includes(sid))
      );
    } else if (effSoc.length > 0) {
      matched = disp.utenti.filter(u => effSoc.some(sid => u.societaIds.includes(sid)));
    } else {
      matched = disp.utenti.filter(u => effSedi.some(sid => u.sedeIds.includes(sid)));
    }

    return new Set(matched.map(u => u.id));
  }, [disp, societaAttiva, societaSel, sediSel]);

  // Utenti finali = (derivati - esclusi) ∪ extra, ordinati per nome
  const finalUsers = useMemo<UtenteDisp[]>(() => {
    if (!disp) return [];
    const combined = new Map<number, UtenteDisp>();

    Array.from(derivedUserIds).forEach(id => {
      if (!utentiEsclusi.has(id)) {
        const u = disp.utenti.find(x => x.id === id);
        if (u) combined.set(id, u);
      }
    });
    Array.from(utentiExtra).forEach(id => {
      if (!combined.has(id)) {
        const u = disp.utenti.find(x => x.id === id);
        if (u) combined.set(id, u);
      }
    });

    return Array.from(combined.values()).sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
  }, [disp, derivedUserIds, utentiEsclusi, utentiExtra]);

  // ── Utenti visibili nel tab (con filtro ricerca + topbar) ─────────────────

  const utentiTabVisibili = useMemo<UtenteDisp[]>(() => {
    if (!disp) return [];
    // Deduplica per id (utente in più società appare una volta sola)
    const map = new Map<number, UtenteDisp>();
    for (const u of disp.utenti) {
      if (!map.has(u.id)) {
        // Filtro topbar
        if (societaAttiva && !u.societaIds.includes(societaAttiva.id)) continue;
        map.set(u.id, u);
      }
    }
    let lista = Array.from(map.values());
    if (cercaUtente.trim()) {
      const q = cercaUtente.toLowerCase();
      lista = lista.filter(u => u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }
    return lista;
  }, [disp, societaAttiva, cercaUtente]);

  // ── Toggle selezioni ──────────────────────────────────────────────────────

  const toggleSocieta = (id: number) =>
    setSocietaSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleSede = (id: number) =>
    setSediSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleUtente = (u: UtenteDisp) => {
    if (derivedUserIds.has(u.id)) {
      // Utente derivato da criteri: toggle esclusione
      setUtentiEsclusi(prev => {
        const next = new Set(prev);
        if (next.has(u.id)) next.delete(u.id); else next.add(u.id);
        return next;
      });
    } else {
      // Utente fuori criteri: toggle extra
      setUtentiExtra(prev => {
        const next = new Set(prev);
        if (next.has(u.id)) next.delete(u.id); else next.add(u.id);
        return next;
      });
    }
  };

  const isCheckedUtente = (u: UtenteDisp): boolean => {
    if (utentiEsclusi.has(u.id)) return false;
    if (utentiExtra.has(u.id)) return true;
    return derivedUserIds.has(u.id);
  };

  // Rimuove utente dai finalUsers (sempre possibile)
  const removeFromFinal = (u: UtenteDisp) => {
    if (derivedUserIds.has(u.id)) {
      setUtentiEsclusi(prev => { const n = new Set(prev); n.add(u.id); return n; });
    } else {
      setUtentiExtra(prev => { const n = new Set(prev); n.delete(u.id); return n; });
    }
  };

  // ── Testo criteri di selezione ────────────────────────────────────────────

  const criteriaText = useMemo<string>(() => {
    const parts: string[] = [];
    if (societaAttiva) {
      parts.push(`🏢 ${societaAttiva.nome}`);
    } else {
      societaSel.forEach(sid => {
        const s = disp?.societa.find(x => x.id === sid);
        if (s) parts.push(`🏢 ${s.nome}`);
      });
    }
    sediSel.forEach(sid => {
      const s = disp?.sedi.find(x => x.id === sid);
      if (s) parts.push(`🏠 ${s.nome}`);
    });
    return parts.join(' · ');
  }, [disp, societaAttiva, societaSel, sediSel]);

  // ── Allegati ──────────────────────────────────────────────────────────────

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAllegatiFile(prev => [...prev, ...Array.from(e.target.files ?? [])]);
    e.target.value = '';
  };

  // ── Invio ─────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    setError('');
    if (!oggetto.trim()) { setError('Oggetto obbligatorio.'); return; }
    if (!corpo.trim())   { setError('Testo obbligatorio.'); return; }
    if (societaSel.length === 0 && sediSel.length === 0 && utentiExtra.size === 0 && !societaAttiva) {
      setError('Seleziona almeno una società, sede o utente destinatario.');
      return;
    }

    setSending(true);
    setUploadStatus({});
    setUploadErrors({});
    try {
      // Società effettiva per il Messaggio (usata dal backend per societaId)
      const effSocietaId = societaAttiva?.id
        ?? (societaSel.length > 0 ? societaSel[0] : null)
        ?? disp?.societaIdDefault
        ?? undefined;

      // Criteri → DestinatarioMessaggio (per tracciabilità), + utentiExtra singoli
      const destinatari = [
        ...(societaAttiva ? [{ tipo: 'SOCIETA', id: societaAttiva.id }] : societaSel.map(id => ({ tipo: 'SOCIETA', id }))),
        ...sediSel.map(id => ({ tipo: 'SEDE', id })),
        ...Array.from(utentiExtra).map(id => ({ tipo: 'UTENTE_SINGOLO', id })),
      ];

      const payload = {
        societaId: effSocietaId,
        oggetto:   oggetto.trim(),
        corpo:     corpo.trim(),
        priorita,
        destinatari,
        utentiEsclusi: Array.from(utentiEsclusi),
      };

      const r = await apiFetch<{ success: boolean; id: number; nDestinatari: number }>('/api/messaggi', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Upload allegati con feedback per file
      let hasUploadError = false;
      for (let i = 0; i < allegatiFile.length; i++) {
        setUploadStatus(prev => ({ ...prev, [i]: 'uploading' }));
        try {
          const fd = new FormData();
          fd.append('file', allegatiFile[i]);
          await apiFetch(`/api/messaggi/${r.id}/allegati`, { method: 'POST', body: fd });
          setUploadStatus(prev => ({ ...prev, [i]: 'done' }));
        } catch (uploadErr: any) {
          const msg = uploadErr?.message ?? 'Errore sconosciuto';
          console.error(`[allegati] upload fallito file[${i}]:`, msg);
          setUploadStatus(prev => ({ ...prev, [i]: 'error' }));
          setUploadErrors(prev => ({ ...prev, [i]: msg }));
          hasUploadError = true;
        }
      }

      if (hasUploadError) {
        setError('Messaggio inviato, ma alcuni allegati non sono stati caricati.');
        setSending(false);
        return;
      }

      navigate('/aziendale/messaggi');
    } catch (err: any) {
      setError(err.message ?? 'Errore invio messaggio');
    } finally {
      setSending(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const hasCriteria = societaSel.length > 0 || sediSel.length > 0 || utentiExtra.size > 0 || !!societaAttiva;

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Nuovo Messaggio</h2>
          <p className="bop-page-subtitle">Componi e invia un messaggio interno</p>
        </div>
        <div className="bop-invio-msg__header-actions">
          <button className="bop-btn bop-btn-secondary" onClick={() => navigate('/aziendale/messaggi')}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleSend} disabled={sending || loadingDest}>
            {sending
              ? (Object.values(uploadStatus).some(s => s === 'uploading')
                  ? `Allegati ${Object.values(uploadStatus).filter(s => s === 'done').length}/${allegatiFile.length}...`
                  : 'Invio in corso...')
              : 'Invia Messaggio'}
          </button>
        </div>
      </div>

      {error && <div className="im-alert-error">{error}</div>}

      <div className="im-layout">

        {/* ── Pannello Destinatari ─────────────────────────────────────────── */}
        <div className="bop-card im-panel-dest">

          {/* Header */}
          <div className="im-panel-header">
            <strong>Destinatari</strong>
            {finalUsers.length > 0 && (
              <span className="im-badge">{finalUsers.length} utenti</span>
            )}
          </div>

          {/* Tabs */}
          <div className="im-tabs">
            {(['societa', 'sedi', 'utenti'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTabDest(t)}
                className={`im-tab${tabDest === t ? ' im-tab-active' : ''}`}
              >
                {t === 'societa' ? 'Per società' : t === 'sedi' ? 'Per sede' : 'Utenti specifici'}
              </button>
            ))}
          </div>

          {/* Contenuto tab */}
          <div className="im-tab-content">
            {loadingDest && <div className="im-empty">Caricamento...</div>}

            {/* ── Tab Società ── */}
            {!loadingDest && tabDest === 'societa' && (
              <>
                {societaVisibili.length === 0 && <div className="im-empty">Nessuna società disponibile</div>}
                {societaVisibili.map(s => (
                  <label key={s.id} className={`im-row${societaSel.includes(s.id) || societaAttiva?.id === s.id ? ' im-row-sel' : ''}`}>
                    <input
                      type="checkbox"
                      checked={societaSel.includes(s.id) || societaAttiva?.id === s.id}
                      disabled={!!societaAttiva}
                      onChange={() => toggleSocieta(s.id)}
                    />
                    <div className="im-row-body">
                      <span className="im-row-title">{s.nome}</span>
                      <span className="im-row-sub">{s.nUtenti} utenti</span>
                    </div>
                  </label>
                ))}
              </>
            )}

            {/* ── Tab Sedi ── */}
            {!loadingDest && tabDest === 'sedi' && (
              <>
                {Object.keys(gruppiSedi).length === 0 && (
                  <div className="im-empty">
                    {societaSel.length > 0 || societaAttiva ? 'Nessuna sede per le società selezionate' : 'Nessuna sede disponibile'}
                  </div>
                )}
                {Object.entries(gruppiSedi).map(([nomeSoc, sedi]) => (
                  <div key={nomeSoc}>
                    <div className="im-group-header">{nomeSoc}</div>
                    {sedi.map(s => (
                      <label key={s.id} className={`im-row im-row-indent${sediSel.includes(s.id) ? ' im-row-sel' : ''}`}>
                        <input
                          type="checkbox"
                          checked={sediSel.includes(s.id)}
                          onChange={() => toggleSede(s.id)}
                        />
                        <div className="im-row-body">
                          <span className="im-row-title">{s.nome}</span>
                          <span className="im-row-sub im-row-sub-inline">{s.nUtenti} utenti</span>
                        </div>
                      </label>
                    ))}
                  </div>
                ))}
              </>
            )}

            {/* ── Tab Utenti specifici ── */}
            {!loadingDest && tabDest === 'utenti' && (
              <>
                <div className="im-search-bar">
                  <input
                    className="bop-input"
                    placeholder="Cerca per nome o email..."
                    value={cercaUtente}
                    onChange={e => setCercaUtente(e.target.value)}
                  />
                </div>
                {utentiTabVisibili.length === 0 && (
                  <div className="im-empty">{cercaUtente ? 'Nessun risultato' : 'Nessun utente disponibile'}</div>
                )}
                {utentiTabVisibili.map(u => {
                  const checked = isCheckedUtente(u);
                  const derived = derivedUserIds.has(u.id);
                  return (
                    <label key={u.id} className={`im-row${checked ? ' im-row-sel' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleUtente(u)}
                      />
                      <div className="im-row-body">
                        <span className="im-row-title">{u.nome}</span>
                        <span className="im-row-sub">{u.email}</span>
                      </div>
                      {derived && !utentiEsclusi.has(u.id) && (
                        <span className="im-tag-derived">auto</span>
                      )}
                    </label>
                  );
                })}
              </>
            )}
          </div>

          {/* ── Sezione SELEZIONATI ── */}
          <div className="im-selections">
            <div className="im-selections-title">Selezionati</div>

            {!hasCriteria ? (
              <div className="im-empty-sel">
                Seleziona società, sedi o utenti per vedere i destinatari
              </div>
            ) : (
              <>
                {/* Criteri context */}
                {criteriaText && (
                  <div className="im-criteria-context">{criteriaText}</div>
                )}

                {/* Contatore */}
                {finalUsers.length > 0 && (
                  <div className="im-sel-count">{finalUsers.length} utenti selezionati</div>
                )}
                {finalUsers.length === 0 && (
                  <div className="im-empty-sel">
                    Nessun utente corrisponde ai criteri selezionati
                  </div>
                )}

                {/* Tag utenti finali */}
                <div className="im-tags">
                  {finalUsers.map(u => (
                    <span key={u.id} className="im-tag-utente" onClick={() => removeFromFinal(u)}>
                      {u.nome} <span className="im-tag-x">×</span>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Pannello Composizione ────────────────────────────────────────── */}
        <div className="im-panel-msg">
          <div className="bop-card">
            <div className="im-msg-fields">
              <div className="bop-form-group im-field-main">
                <label>Oggetto *</label>
                <input
                  className="bop-input"
                  value={oggetto}
                  onChange={e => setOggetto(e.target.value)}
                  placeholder="Inserisci l'oggetto del messaggio"
                  maxLength={200}
                />
              </div>
              <div className="bop-form-group im-priorita-col">
                <label>Priorità</label>
                <select className="bop-select" value={priorita} onChange={e => setPriorita(e.target.value)}>
                  {PRIORITA.map(p => (
                    <option key={p} value={p}>{PRIORITA_LABEL[p]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="im-priorita-indicator">
              <span className="im-priorita-dot" style={{ background: PRIORITA_COLOR[priorita] }} />
              <span className="im-priorita-text">Priorità: <strong>{PRIORITA_LABEL[priorita]}</strong></span>
            </div>

            <div className="bop-form-group">
              <label>Testo *</label>
              <textarea
                className="bop-input im-textarea"
                value={corpo}
                onChange={e => setCorpo(e.target.value)}
                rows={12}
                placeholder="Scrivi il testo del messaggio..."
              />
            </div>
          </div>

          {/* Allegati */}
          <div className="bop-card">
            <div className="im-allegati-header">
              <strong className="im-allegati-title">Allegati</strong>
              <button className="bop-btn bop-btn-secondary im-btn-sm" onClick={() => fileRef.current?.click()}>
                + Aggiungi file
              </button>
              <input ref={fileRef} type="file" multiple className="bop-invio-msg__file-hidden" onChange={handleFileAdd} />
            </div>

            {allegatiFile.length === 0 ? (
              <div
                className="im-drop-zone"
                onClick={() => fileRef.current?.click()}
                onDrop={e => { e.preventDefault(); setAllegatiFile(prev => [...prev, ...Array.from(e.dataTransfer.files)]); }}
                onDragOver={e => e.preventDefault()}
              >
                Trascina i file qui o clicca per selezionare
              </div>
            ) : (
              <div className="im-allegati-list">
                {allegatiFile.map((f, i) => {
                  const st = uploadStatus[i] ?? 'idle';
                  return (
                    <div key={i} className="im-allegato-row">
                      <span className="im-allegato-name">
                        {f.name} <span className="im-allegato-size">({(f.size / 1024).toFixed(0)} KB)</span>
                      </span>
                      {st === 'uploading' && (
                        <span className="im-upload-uploading">⏳</span>
                      )}
                      {st === 'done' && (
                        <span className="im-upload-done">✓</span>
                      )}
                      {st === 'error' && (
                        <span className="im-upload-error" title={uploadErrors[i] ?? 'Errore upload'}>✕ {uploadErrors[i] ?? 'errore'}</span>
                      )}
                      {st === 'idle' && (
                        <button
                          className="im-remove-btn"
                          onClick={() => setAllegatiFile(prev => prev.filter((_, j) => j !== i))}
                        >×</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
